import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { Observable, Subject, from, fromEvent, map, mergeMap, takeUntil } from 'rxjs';
import { ElementType } from '../../models/element-type';
import { Footer } from '../../models/footer';
import { Menu } from '../../models/menu';
import { Page } from '../../models/page';
import { PageRequest } from '../../models/page-request';
import { Settings } from '../../models/settings';
import { ElementService } from '../../services/element.service';
import { HistoryService } from '../../services/history.service';
import { ModalService } from '../../services/modal.service';
import { RelayService } from '../../services/relay.service';
import { DynamicState, SetFooter, SetMenu, SetPage } from '../../stores/dynamic.store';
import { SetSettings } from '../../stores/main.store';
import { Modal } from '../../models/modal';
import { Confirm } from '../../models/confirm';
import { PortalUpdateEvent } from '../../models/portal-update-event';
import { PortalActionEvent } from '../../models/portal-action-event';
import { PortalErrorEvent } from '../../models/portal-error-event';
import { PageComponent } from '../dynamic-elements/page/page.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'portal',
    templateUrl: 'portal.component.html',
    imports: [PageComponent, AsyncPipe],
})
export class PortalComponent implements OnInit, OnDestroy {
  @ViewChild('portal-page') root: ElementRef | null = null;

  menu$: Observable<Menu> | undefined;
  page$: Observable<Page> | undefined;
  footer$: Observable<Footer> | undefined;

  destroy$ = new Subject<void>();

  protected historyEnabled: boolean = true;

  constructor(
    protected relayService: RelayService,
    protected elementService: ElementService,
    protected historyService: HistoryService,
    protected store: Store,
    protected modalService: ModalService
  ) {
    this.setPageHistory();
  }

  setPageHistory() {
    if (this.historyEnabled) {
      var currentPageRequest = { pageId: this.store.selectSnapshot(DynamicState.page)?.id } as PageRequest;
      if (!history.state || !history.state.pageId) {
        this.historyService.replaceHistory(currentPageRequest);
      }
    }
  }

  async ngOnInit() {
    this.activateMenu();
    this.activatePage();
    this.activateFooter();
    this.activateHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  activateHistory() {
    if (this.historyEnabled) {
      fromEvent(window, 'popstate')
        .pipe(
          takeUntil(this.destroy$),
          map((e) => e as PopStateEvent)
        )
        .subscribe(($event: PopStateEvent) => this.popState($event));
    }
  }

  popState($event: PopStateEvent) {
    if ($event.state && $event.state.pageId != null) {
      this.relayService.getPage($event.state, false, $event.target ?? undefined);
    }
  }

  protected activateMenu() {
    this.menu$ = this.store.select(DynamicState.menu).pipe(mergeMap((e) => from(this.displayMenuPipe(e))));
    this.menu$.pipe(takeUntil(this.destroy$)).subscribe((e) => this.displayMenu(e));
  }

  protected activatePage() {
    this.page$ = this.store.select(DynamicState.page).pipe(mergeMap((e) => from(this.displayPagePipe(e))));
    this.page$.pipe(takeUntil(this.destroy$)).subscribe((e) => this.displayPage(e));
  }

  protected activateFooter() {
    this.footer$ = this.store.select(DynamicState.footer).pipe(mergeMap((e) => from(this.displayFooterPipe(e))));
    this.footer$.pipe(takeUntil(this.destroy$)).subscribe((e) => this.displayFooter(e));
  }

  // Global updates means that we expect events to come to just window at times
  // This will cause the component to react to any portal update event that bubbles up
  // If two or more portal components are present in scope with these options set they will
  // both receive the same updates.

  protected activateGlobalUpdateEvents() {
    fromEvent(window, PortalUpdateEvent.EVENT_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.updateEvent(e));
  }

  protected activateGlobalActionEvents() {
    fromEvent(window, PortalActionEvent.EVENT_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.actionEvent(e));
  }

  protected activateGlobalErrorEvents() {
    fromEvent(window, PortalErrorEvent.EVENT_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.errorEvent(e));
  }

  protected activateGlobalEvents() {
    this.activateGlobalUpdateEvents();
    this.activateGlobalActionEvents();
    this.activateGlobalErrorEvents();
  }

  protected updateEvent(event: any) {
    event.stopPropagation();
    event.preventDefault();
    this.updateHistory(event.detail);
    this.updateStore(event);
  }

  protected updateHistory($event: PortalUpdateEvent) {
    if (this.historyEnabled && ElementType.page === $event.element.elementType) {
      var element = $event.element as Page;
      if (
        history.state !== $event.request &&
        ((element as Page).saveToHistory ||
          (element as Page).saveToHistory == null ||
          (element as Page).saveToHistory == undefined)
      ) {
        this.historyService.pushHistory($event.request);
      }
    }
  }

  protected async updateStore($event: any) {
    var updateEvent = $event.detail as PortalUpdateEvent;
    switch (updateEvent.element.elementType) {
      case ElementType.menu:
        this.updateMenuStore(updateEvent);
        break;
      case ElementType.page:
        this.updatePageStore(updateEvent);
        break;
      case ElementType.footer:
        this.updateFooterStore(updateEvent);
        break;
      case ElementType.settings:
        this.store.dispatch(new SetSettings(updateEvent.element as Settings));
        break;
      case ElementType.confirm:
        await this.elementService.initializeElement(updateEvent.element);
        this.modalService.confirm(updateEvent.element as Confirm);
        break;
      case ElementType.modal:
        await this.elementService.initializeElement(updateEvent.element);
        this.modalService.modal(updateEvent.element as Modal, $event.target);
        break;
    }
  }

  protected updateMenuStore($event: PortalUpdateEvent) {
    this.store.dispatch(new SetMenu($event.element as Menu));
  }

  protected updatePageStore($event: PortalUpdateEvent) {
    var element = $event.element;
    element.id = $event.request!.pageId;
    this.store.dispatch(new SetPage(element as Page));
  }

  protected updateFooterStore($event: PortalUpdateEvent) {
    this.store.dispatch(new SetFooter($event.element as Footer));
  }

  protected async actionEvent($event: any) {
    const detail = $event.detail as PortalActionEvent;
    if (!detail || detail.type !== 'SUBMIT') {
      return;
    }

    let element = detail.element;
    if (!element) {
      return;
    }

    if (element.confirmAction) {
      if (!(await this.modalService.confirm(element.confirmAction))) {
        return;
      }
    }

    if (element.pageRequests && element.pageRequests.length) {
      for await (const pageRequest of element.pageRequests) {
        let result = await this.relayService.getPage(pageRequest, true, $event.target);
      }
    }

    if (element.modalAction) {
      await this.modalService.modal(element.modalAction, $event.target);
    }
  }

  protected errorEvent($event: any) {
    this.modalService.error($event.detail.errorMessage);
  }

  protected async displayMenuPipe(element: Menu | null): Promise<Menu> {
    return await this.elementService.initializeElement(cloneDeep(element ?? ({} as Menu))) as Menu;
  }

  protected async displayPagePipe(element: Page | null): Promise<Page> {
    return await this.elementService.initializeElement(cloneDeep(element ?? ({} as Page))) as Page;
  }

  protected async displayFooterPipe(element: Footer | null): Promise<Footer> {
    return await this.elementService.initializeElement(cloneDeep(element ?? ({} as Footer))) as Footer;
  }

  protected displayMenu(element: Menu) {}
  protected displayPage(element: Page) {}
  protected displayFooter(element: Footer) {}
}
